import {
  useState, useCallback, useEffect, useContext
} from 'react';

import {
  getDigestionItemsForPaidHolidayStatus,
  downloadPaidHolidayStatusCsv,
  getPaidHolidayStatusDetail,
  saveUseItemForPaidHolidayStatus,
} from 'api/holidayManagement';
import PaidHolidayStatusDomain from 'domain/master/holidayManagement/paidHolidayStatus';
import useToastNotification from 'hooks/useToastNotification';
import { HolidayManagementContext } from './../../HolidayManagementForm'

export interface DetailItems {
  paidHolidayGrantId: string,
  staffCode: string,
  lastFirstGrantDaysNum: number,
  lastFirstGrantTimesNum: number,
  lastGrantDaysNum: number,
  lastGrantTimesNum: number,
  firstGrantDaysNum: number,
  firstGrantTimesNum: number,
  grantDaysNum: number,
  grantTimesNum: number,
  useLastFirstGrantFlg: boolean,
  useFirstGrantFlg: boolean,
  useCurrentGrantFlg: boolean,
  useLastGrantFlg: boolean,
}

export const useManagementInformationDomainForm = () => {
  const sessionStorageOrgCode = sessionStorage.getItem('loginUser.orgCode') || '';
  const [paidHolidayStatusList, SetPaidHolidayStatusList] = useState<Array<PaidHolidayStatusDomain>>([]);
  const [noManager, setNoManager] = useState(false);
  const [orgCode, setOrgCode] = useState(sessionStorageOrgCode);
  const [employmentId, setOrgEmploymentId] = useState('');
  const [extinctionDaysStart, setExtinctionDaysStart] = useState('');
  const [extinctionDaysEnd, setExtinctionDaysEnd] = useState('');
  const [extinctionDaysStartTmp, setExtinctionDaysStartTmp] = useState('');
  const [extinctionDaysEndTmp, setExtinctionDaysEndTmp] = useState('');
  const [targetGrantTenDays, setTargetGrantTenDays] = useState(false);
  const [untilNextGrantDate, setUntilNextGrantDate] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [detailItems, setDetailItems] = useState<DetailItems>({
    paidHolidayGrantId: '',
    staffCode: '',
    lastFirstGrantDaysNum: 0,
    lastFirstGrantTimesNum: 0,
    lastGrantDaysNum: 4,
    lastGrantTimesNum: 3,
    firstGrantDaysNum: 0,
    firstGrantTimesNum: 0,
    grantDaysNum: 0,
    grantTimesNum: 0,
    useLastFirstGrantFlg: false,
    useFirstGrantFlg: false,
    useCurrentGrantFlg: false,
    useLastGrantFlg: false,
  });
  const { successNotification, errorNotification } = useToastNotification();

  const contextObject = useContext(HolidayManagementContext);

  // データの取得
  const fetchData = useCallback(async () => {
    contextObject.setIsLoading(true);
    // const orgCodeList = orgTreesOptions.filter((orgOption: any) => orgOption.value !== '').map((value:any) =>  value.value ).join(",");
    const response = await getDigestionItemsForPaidHolidayStatus(
      orgCode !== 'all' ? orgCode : '',
      extinctionDaysStart,
      extinctionDaysEnd,
      employmentId,
      targetGrantTenDays,
      // untilNextGrantDate
      ''
    );
    contextObject.setIsLoading(false);
    SetPaidHolidayStatusList(response.map((result) => new PaidHolidayStatusDomain(result)));
  }, [
    employmentId, 
    extinctionDaysEndTmp,
    // untilNextGrantDate, 
    extinctionDaysStartTmp,
    orgCode, targetGrantTenDays
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, noManager, orgCode, employmentId, targetGrantTenDays]);

  const toggleNoManager = useCallback(() => {
    setNoManager(!noManager);
  }, [noManager]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  //
  const callPaidHolidayStatusDetail = useCallback(async (staffCode: string, paidHolidayGrantId: string) => {
    try {
      await getPaidHolidayStatusDetail(staffCode, paidHolidayGrantId).then((response: any) => {
        setDetailItems(response);
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const listErr = error.response.data.errors;
        listErr.map((element: any) => errorNotification(element.defaultMessage));
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
        throw error;
      }
    }
  }, [errorNotification]);

  //
  const callSaveUseItemForPaidHolidayStatus = useCallback(async () => {
    contextObject.setIsLoading(true);
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    const tmpDataPost = detailItems;
    (tmpDataPost as any).saveUser = tmpDataPost.staffCode;
    if (!(tmpDataPost as any).paidHolidayGrantId) {
      (tmpDataPost as any).paidHolidayGrantId = '';
    }
    try {
      await saveUseItemForPaidHolidayStatus(companyCode, tmpDataPost).then((response: any) => {
        setNoManager(!noManager);
        successNotification('更新しました。');
        setModalOpen(false);
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const listErr = error.response.data.errors;
        let stringErr = '';
        listErr.map((element: any) => {
          stringErr += `${element.defaultMessage}<br />`;
          return stringErr;
        });
        errorNotification(stringErr);
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
        throw error;
      }
    } finally {
      contextObject.setIsLoading(false);
    }
  }, [detailItems, errorNotification, noManager, successNotification]);

  return {
    paidHolidayStatusList,
    toggleNoManager,
    orgCode,
    setOrgCode,
    employmentId,
    setOrgEmploymentId,
    extinctionDaysStart,
    setExtinctionDaysStart,
    extinctionDaysEnd,
    setExtinctionDaysEnd,
    targetGrantTenDays,
    setTargetGrantTenDays,
    modalOpen,
    setModalOpen,
    closeModal,
    detailItems,
    setDetailItems,
    callPaidHolidayStatusDetail,
    callSaveUseItemForPaidHolidayStatus,
    setUntilNextGrantDate,
    untilNextGrantDate,
    extinctionDaysStartTmp,
    setExtinctionDaysStartTmp,
    extinctionDaysEndTmp,
    setExtinctionDaysEndTmp
  };
};

export const useDownloadHolidayManagement = (
  orgCode: string,
  extinctionDaysStart: string,
  extinctionDaysEnd: string,
  employmentId: string,
  targetGrantTenDays: boolean,
) => {
  const [blocking, setBlocking] = useState(false);
  const downloadPaidHolidayStatus = useCallback((isDownloadAll?: boolean) => {
    setBlocking(true);
    downloadPaidHolidayStatusCsv(
      isDownloadAll ? '' : orgCode,
      extinctionDaysStart,
      extinctionDaysEnd,
      employmentId,
      targetGrantTenDays,
    ).then((response) => {
      setBlocking(false);
    });
  }, [employmentId, extinctionDaysEnd, extinctionDaysStart, orgCode, targetGrantTenDays]);

  return {
    downloadPaidHolidayStatus,
    blocking
  };
};

export default {
  useManagementInformationDomainForm,
};
